import React from 'react';

import UsersClient from 'src/clients/UsersClient';
import { LockOpenIcon, LogoutIcon, PlusIcon } from 'src/components/icons';
import { WorkspaceAvatar } from 'src/components/WorkspaceAvatar';
import { PortalConfigContext } from 'src/context';
import history from 'src/history';
import { useLogout } from 'src/hooks/useLogout';
import { useWorkspaceMenuPaths } from 'src/hooks/useWorkspaceMenuPaths';
import { Action, BaseMenu } from 'src/legacy/components/Dropdowns';
import { AuthenticatedPortalDataType } from 'src/legacy/components/Onboarding/Login/onboardingLoginTypes';
import { getPublicImageURL } from 'src/utils/ImageUtils';

import { SidebarSwitcherButton } from './SidebarSwitcherButton';

const getListOfWorkspaces = (
  wp: Record<string, Record<string, AuthenticatedPortalDataType>>,
) =>
  Object.entries(wp).map(([email, workspacesMap]) =>
    Object.entries(workspacesMap).map(([, workspace]) => ({
      ...workspace,
      email,
    })),
  );

const getWorkspaceActions = (
  spaces: {
    name: string;
    onClick: () => void;
    avatar: JSX.Element;
    email: string;
  }[],
): Action[] => {
  const sortedWs: Action[] = [];
  let currentEmail = '';
  spaces.forEach((ws) => {
    if (currentEmail !== ws.email) {
      if (sortedWs[sortedWs.length - 1]) {
        sortedWs[sortedWs.length - 1].hasDivider = true;
      }
      sortedWs.push({
        name: ws.email,
        isSubtitle: true,
        classes: ['pl-2'],
      } as Action);
      currentEmail = ws.email;
    }
    sortedWs.push({ ...ws, classes: ['pl-2'] });
  });
  if (sortedWs[sortedWs.length - 1]) {
    sortedWs[sortedWs.length - 1].hasDivider = true;
  }
  return sortedWs;
};

interface WorkspaceSelectorProps {
  workspaces: Record<string, Record<string, AuthenticatedPortalDataType>>;
  iconSrc?: string;
  iconFallbackColor?: string;
  iconSize?: number;
  /** Optional avatar element forwarded to the switcher button (e.g. a branded company avatar). */
  iconSlot?: React.ReactNode;
  name: string;
  userIsClient: boolean;
  isTestUser: boolean;
}

export const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
  workspaces,
  iconSrc,
  iconFallbackColor,
  iconSize,
  iconSlot,
  name,
  userIsClient,
  isTestUser,
}) => {
  const portalConfig = React.useContext(PortalConfigContext);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const [menuActions, setMenuActions] = React.useState<Action[]>([]);
  const accounts = Object.keys(workspaces).reduce(
    (acc, key) => acc + Object.keys(workspaces[key]).length,
    0,
  );
  const logout = useLogout();
  const { internalLogoutRedirectPath, createWorkspacePath, addAccountPath } =
    useWorkspaceMenuPaths();

  // Keep a ref so static-action callbacks always call the latest logout
  // without making it a useMemo/useEffect dependency (logout is not memoized).
  const logoutRef = React.useRef(logout);
  logoutRef.current = logout;

  // Extract primitives from portalConfig so they can be stable dep array entries.
  const { portalHeader } = portalConfig;
  const { userPoolWebClientId } = portalConfig.AWS.Auth;
  const signoutRedirectURL = portalConfig.SignoutRedirectURL;

  // Static actions (logout / login / create workspace) depend on user state,
  // not on the workspaces list. Memoised separately so the workspace useEffect
  // below only re-runs when the workspace list or portal identity changes.
  const staticActions = React.useMemo<Action[]>(() => {
    const logoutAction: Action = {
      name: accounts > 1 ? 'Log out of all accounts' : 'Log out',
      icon: <LogoutIcon style={{ fontSize: 16 }} />,
      onClick: () =>
        logoutRef.current(
          isTestUser,
          !userIsClient ? internalLogoutRedirectPath : signoutRedirectURL,
          userIsClient,
        ),
      iconStyle: { height: 28, width: 28 },
      classes: ['pl-2'],
    };
    if (userIsClient) return [logoutAction];
    return [
      {
        name: 'Log in to another workspace',
        icon: <LockOpenIcon style={{ fontSize: 16 }} />,
        onClick: () => {
          history.replace(addAccountPath);
          window.location.reload();
        },
        iconStyle: { height: 28, width: 28 },
        classes: ['pl-2'],
      },
      {
        name: 'Create a new workspace',
        icon: <PlusIcon style={{ fontSize: 16 }} />,
        onClick: () => {
          history.replace(createWorkspacePath);
          window.location.reload();
        },
        iconStyle: { height: 28, width: 28 },
        classes: ['pl-2'],
      },
      logoutAction,
    ];
  }, [
    accounts,
    isTestUser,
    userIsClient,
    signoutRedirectURL,
    internalLogoutRedirectPath,
    createWorkspacePath,
    addAccountPath,
  ]);

  React.useEffect(() => {
    const workspacesActions = getListOfWorkspaces(workspaces)
      .flat()
      .filter((workspace) => workspace.id !== portalHeader)
      .map(({ name: wsName, id, iconUrl, email, hostname }) => ({
        name: wsName,
        onClick: async () => {
          const result = await UsersClient.setExtraPortalSession(
            id,
            hostname,
            email,
          );
          if (result.data.userId) {
            window.localStorage.setItem(
              `CognitoIdentityServiceProvider.${userPoolWebClientId}.LastAuthUser`,
              result.data.userId,
            );
          }
          history.replace(result.data.redirectUrl);
          window.location.reload();
        },
        avatar: (
          <WorkspaceAvatar
            iconSrc={getPublicImageURL(iconUrl)}
            workspaceName={wsName}
          />
        ),
        email,
      }));

    setMenuActions([
      ...getWorkspaceActions(workspacesActions),
      ...staticActions,
    ]);
  }, [workspaces, portalHeader, userPoolWebClientId, staticActions]);

  return (
    <>
      <SidebarSwitcherButton
        ref={anchorRef}
        iconSrc={iconSrc}
        iconFallbackColor={iconFallbackColor}
        iconSize={iconSize}
        name={name}
        isOpen={open}
        onClick={() => setOpen(true)}
        iconSlot={iconSlot}
      />
      <BaseMenu
        actions={menuActions}
        menuProps={{
          style: { left: '-8px' },
          id: 'actions-menu',
          'data-testid': 'workspaces-menu',
          anchorEl: anchorRef.current,
          keepMounted: true,
          open,
          onClose: () => setOpen(false),
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          TransitionProps: {
            onEntering: (node) => {
              const list = node.querySelector('ul');
              if (list && list.style.width) {
                list.style.paddingRight = '11px';
                list.style.width = 'calc(100% + 11px)';
              }
            },
          },
        }}
      />
    </>
  );
};
